/**
 * Layer Assignment
 * 
 * Assigns tasks to layers using topological sort based on LONGEST PATH,
 * then applies "pull-down" (late scheduling) to minimize long edges.
 * 
 * Layer 0 = tasks with no forward dependencies (roots)
 * Layer N = tasks whose longest path from any root is N
 * 
 * PULL-DOWN / LATE SCHEDULING:
 * After initial BFS assignment, nodes are pulled down to sit as close as
 * possible to their children. This prevents a root node that connects to
 * a node deep in a chain from being stranded at layer 0 with a very long
 * edge. Each node is moved to max(parentLayer+1, minChildLayer-1).
 * 
 * FEEDBACK LOOP HANDLING:
 * - Critical path nodes are assigned to layers normally
 * - Feedback nodes are NOT added to layers - they're positioned separately
 * - Nested feedback loops are processed level by level
 * 
 * @see ./pullDownNodes.js - Pull-down / late scheduling logic
 * @see ./feedbackLoopAssignment.js - Feedback loop level-by-level processing
 */

import { pullDownNodes } from './pullDownNodes';
import { assignFeedbackLayers } from './feedbackLoopAssignment';

/**
 * Assign tasks to layers based on forward dependencies only.
 * 
 * PHASE 1: Layout the CRITICAL PATH first (level 0 nodes)
 * PHASE 2: Process feedback loops level by level (level 1, then 2, etc.)
 */
export function assignLayers(tasks, graph) {
  // Sort tasks by ID for deterministic ordering
  const sortedTasks = [...tasks].sort((a, b) => (a.id || '').localeCompare(b.id || ''));
  
  // Get feedback nodes and their levels
  const feedbackNodes = graph.feedbackNodes || new Set();
  const nodeLevels = graph.nodeLevels || new Map();
  
  // PHASE 1: Only consider non-feedback nodes for the critical path
  const criticalPathTasks = sortedTasks.filter(task => !feedbackNodes.has(task.id));
  
  const layers = [];
  const assigned = new Set();
  const taskLayers = new Map();

  // Find all root nodes (no FORWARD dependencies) from critical path tasks only
  // A node is a TRUE root only if it has no forward parents at all.
  // Nodes whose only parents are feedback nodes are NOT roots - they should be
  // placed below their feedback parent's position (handled in post-processing).
  const roots = criticalPathTasks.filter(task => {
    const forwardParents = graph.parents.get(task.id) || [];
    // True root: no forward parents at all
    if (forwardParents.length === 0) return true;
    // Has non-feedback parents: root if all non-feedback parents are satisfied
    const nonFeedbackParents = forwardParents.filter(pid => !feedbackNodes.has(pid));
    if (nonFeedbackParents.length > 0) return false; // Has critical path parents, not a root
    // Only has feedback parents: NOT a root (will be handled in post-processing)
    return false;
  });

  // If no roots, find nodes with minimum in-degree
  if (roots.length === 0 && criticalPathTasks.length > 0) {
    let minInDegree = Infinity;
    criticalPathTasks.forEach(task => {
      const forwardParents = graph.parents.get(task.id) || [];
      const nonFeedbackParents = forwardParents.filter(pid => !feedbackNodes.has(pid));
      if (nonFeedbackParents.length < minInDegree) minInDegree = nonFeedbackParents.length;
    });
    
    const pseudoRoots = criticalPathTasks.filter(task => {
      const forwardParents = graph.parents.get(task.id) || [];
      const nonFeedbackParents = forwardParents.filter(pid => !feedbackNodes.has(pid));
      return nonFeedbackParents.length === minInDegree;
    });
    
    if (pseudoRoots.length > 0) {
      roots.push(...pseudoRoots);
    }
  }
  
  // Handle edge case: no critical path tasks
  if (roots.length === 0) {
    console.log('[LayerAssignment] WARNING: No critical path - all nodes are in feedback loops');
    layers.push(tasks.map(t => t.id));
    tasks.forEach(t => taskLayers.set(t.id, 0));
    return { layers, taskLayers, feedbackLayerInfo: new Map() };
  }

  // BFS to assign layers for CRITICAL PATH ONLY
  let currentLayer = roots.map(t => t.id);
  let layerIndex = 0;

  while (currentLayer.length > 0) {
    layers.push([...currentLayer]);
    currentLayer.forEach(id => {
      assigned.add(id);
      taskLayers.set(id, layerIndex);
    });

    const nextLayer = [];
    criticalPathTasks.forEach(task => {
      if (assigned.has(task.id)) return;
      
      const forwardParents = graph.parents.get(task.id) || [];
      const relevantParents = forwardParents.filter(pid => !feedbackNodes.has(pid));
      
      if (relevantParents.length > 0 && relevantParents.every(pid => assigned.has(pid))) {
        nextLayer.push(task.id);
      }
    });

    currentLayer = nextLayer;
    layerIndex++;

    if (layerIndex > tasks.length) break;
  }
  
  // POST-PROCESSING: Pull down nodes to minimize long edges
  pullDownNodes(layers, taskLayers, graph, feedbackNodes);
  
  // POST-PROCESSING: Handle critical path nodes whose only parents are feedback nodes.
  // These nodes weren't reached by BFS because their only parents are feedback nodes.
  // Place them below their feedback parent's effective position in the graph.
  const unassignedCritical = criticalPathTasks.filter(task => !assigned.has(task.id));
  if (unassignedCritical.length > 0) {
    for (const task of unassignedCritical) {
      const forwardParents = graph.parents.get(task.id) || [];
      let maxParentLayer = 0;
      
      for (const pid of forwardParents) {
        if (feedbackNodes.has(pid)) {
          // Use the feedback node's parent layer
          const feedbackParents = graph.allParents.get(pid) || [];
          for (const fpid of feedbackParents) {
            if (taskLayers.has(fpid)) {
              maxParentLayer = Math.max(maxParentLayer, taskLayers.get(fpid) + 1);
            }
          }
          const feedbackChildren = graph.allChildren.get(pid) || [];
          for (const fcid of feedbackChildren) {
            if (taskLayers.has(fcid)) {
              maxParentLayer = Math.max(maxParentLayer, taskLayers.get(fcid) + 1);
            }
          }
        } else if (taskLayers.has(pid)) {
          maxParentLayer = Math.max(maxParentLayer, taskLayers.get(pid) + 1);
        }
      }
      
      const targetLayer = Math.max(maxParentLayer, layers.length);
      
      while (layers.length <= targetLayer) {
        layers.push([]);
      }
      layers[targetLayer].push(task.id);
      assigned.add(task.id);
      taskLayers.set(task.id, targetLayer);
    }
  }

  // PHASE 2: Process feedback loops level by level
  const feedbackLayerInfo = assignFeedbackLayers(
    sortedTasks, layers, taskLayers, graph, feedbackNodes, nodeLevels
  );

  return { layers, taskLayers, feedbackLayerInfo };
}
